import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { appRoutes } from '../../../../../path';
import { useAppDispatch } from '../../../../../redux/hooks';
import { ModalSliceType, openModal } from '../../../../../redux/slices/modal';
import { assertNotNil } from '../../../../../utils/types';
import useDashboardSelector from '../../../../../redux/selectorHooks/useDashboardSelector';
import {
  IDashboardEntityPermissions,
  IDashboardEntityPermissionsHint,
} from '../../../../../utils/hooks/permissionHooks/types';
import ActionsMenu from '../../../../../components/ActionsMenu';
import { SingleSelectOption } from '../../../../../components/Dropdown/SingleSelect';
import ThinDivider from '../../../../../components/ThinDivider';
import KubitTooltip from '../../../../../components/KubitTooltip';

interface IProps {
  onRefresh: () => void;
  dashboardBaseId: string;
  dashboardEntityPermissions: IDashboardEntityPermissions;
  dashboardEntityPermissionsHint: IDashboardEntityPermissionsHint;
  isDisabled: boolean;
}

const DashboardOptions = ({
  onRefresh,
  dashboardBaseId,
  dashboardEntityPermissions,
  dashboardEntityPermissionsHint,
  isDisabled,
}: IProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const dashboard = useDashboardSelector('dashboard');
  assertNotNil(dashboard);

  const cloneModalHandler = () => {
    dispatch(
      openModal({
        type: ModalSliceType.DashboardDuplicate,
        data: {
          type: 'instance',
          instance: dashboard,
        },
      }),
    );
  };

  const dashboardMoveToHandler = () => {
    if (dashboard.isPublished) {
      dispatch(
        openModal({ type: ModalSliceType.DashboardMove, data: dashboard }),
      );
    } else {
      dispatch(
        openModal({ type: ModalSliceType.DashboardPublish, data: dashboard }),
      );
    }
  };

  const dashboardDeleteHandler = () => {
    dispatch(
      openModal({ type: ModalSliceType.DashboardDelete, data: dashboard }),
    );
  };

  const toggleSchedulerModal = useCallback(() => {
    dispatch(
      openModal({
        type: ModalSliceType.CreateSchedule,
        id: `dashboard-${dashboardBaseId}`,
      }),
    );
  }, [dashboardBaseId, dispatch]);

  return (
    <KubitTooltip
      content={'Options are not available while reports are loading.'}
      placement="bottom-end"
      disabled={!isDisabled}
      noOpenDelay={true}
    >
      <ActionsMenu disabled={isDisabled} menuMinWidth={180}>
        <SingleSelectOption
          iconName="refresh"
          label={'Refresh'}
          onClick={onRefresh}
          disabled={!dashboardEntityPermissions.refreshDashboard}
          tooltipOption={
            !dashboardEntityPermissions.refreshDashboard
              ? { content: dashboardEntityPermissionsHint.refreshDashboard }
              : undefined
          }
        />
        <ThinDivider layout="horizontal" size="full" />
        <SingleSelectOption
          iconName="schedule"
          label={'Create New Schedule'}
          onClick={toggleSchedulerModal}
          disabled={!dashboardEntityPermissions.manageSchedules}
          tooltipOption={
            !dashboardEntityPermissions.manageSchedules
              ? { content: dashboardEntityPermissionsHint.manageSchedules }
              : undefined
          }
        />
        <SingleSelectOption
          iconName="clock"
          label={'Existing Schedules'}
          onClick={() => navigate(appRoutes.schedules.dashboard)}
          disabled={!dashboardEntityPermissions.viewSchedules}
          tooltipOption={
            !dashboardEntityPermissions.viewSchedules
              ? { content: dashboardEntityPermissionsHint.viewSchedules }
              : undefined
          }
        />
        <ThinDivider layout="horizontal" size="full" />
        <SingleSelectOption
          iconName="duplicate"
          label={'Duplicate'}
          onClick={cloneModalHandler}
          disabled={!dashboardEntityPermissions.duplicateDashboard}
          tooltipOption={
            !dashboardEntityPermissions.duplicateDashboard
              ? { content: dashboardEntityPermissionsHint.duplicateDashboard }
              : undefined
          }
        />
        <SingleSelectOption
          iconName="folder-open"
          label={dashboard.isPublished ? 'Move To' : 'Publish To'}
          onClick={dashboardMoveToHandler}
          disabled={!dashboardEntityPermissions.moveAndPublish}
          tooltipOption={
            !dashboardEntityPermissions.moveAndPublish
              ? { content: dashboardEntityPermissionsHint.moveAndPublish }
              : undefined
          }
        />
        <ThinDivider layout="horizontal" size="full" />
        <SingleSelectOption
          iconName="trash"
          label={'Delete Dashboard'}
          onClick={dashboardDeleteHandler}
          disabled={!dashboardEntityPermissions.deleteDashboard}
          tooltipOption={
            !dashboardEntityPermissions.deleteDashboard
              ? { content: dashboardEntityPermissionsHint.deleteDashboard }
              : undefined
          }
        />
      </ActionsMenu>
    </KubitTooltip>
  );
};

export default DashboardOptions;
